import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogOut, Star } from 'lucide-react';
import { UserStats } from '../../app/types';
import { type AccountRole } from '../../accounts';
import { getLearnerNavItems } from './navigationHelpers';
import { getAccountRoleLabel } from '../../accounts';
import { APP_NAME, getProgressToNextLevel } from '../../config/constants';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  displayName: string;
  activeAccountRole?: AccountRole;
  onLogout?: () => void;
}
export function MobileNav({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  stats,
  displayName,
  activeAccountRole = 'learner',
  onLogout
}: MobileNavProps) {
  // Prevent body scroll when menu is open
  useBodyScrollLock(isOpen);
  const {
    accounts
  } = useAppStore();
  const navItems = getLearnerNavItems();
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  const streak = stats?.xp != null ? accounts?.accountsById[accounts?.activeAccountId || '']?.appState?.streak?.currentTotalStreak || 0 : 0;
  return <AnimatePresence data-id="element-1087">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden" data-id="element-1088" />

          {/* Drawer */}
          <motion.div initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 left-0 w-[85%] max-w-sm bg-[#FAFAF8] shadow-2xl z-50 md:hidden flex flex-col rounded-r-[2.5rem] border-r-2 border-white" data-id="element-1089">
            {/* Header */}
            <div className="p-6 border-b-2 border-slate-100 flex items-center justify-between bg-white rounded-tr-[2.5rem]" data-id="element-1090">
              <div className="flex items-center gap-3" data-id="element-1091">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-md transform rotate-3" data-id="element-1092">
                  <span className="text-white font-black text-xl" data-id="element-1093">M</span>
                </div>
                <span className="font-black text-slate-800 text-xl tracking-tight" data-id="element-1094">
                  {APP_NAME}
                </span>
              </div>
              <button onClick={onClose} className="p-2 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 rounded-full transition-colors" aria-label="Sluiten" data-id="element-1095">
                <X className="w-6 h-6 stroke-[2.5]" data-id="element-1096" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-6 px-5 space-y-8" data-id="element-1097">
              {/* User Profile Gamified - Learner */}
              {activeAccountRole === 'learner' && <div className="flex items-center gap-4 p-5 bg-white rounded-3xl border-2 border-slate-100 shadow-sm relative overflow-hidden" data-id="element-1098">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-1099"></div>

                  <div className="relative z-10" data-id="element-1100">
                    {/* SVG Progress Ring */}
                    <svg className="absolute -inset-1.5 w-[76px] h-[76px] transform -rotate-90" viewBox="0 0 76 76" data-id="element-1101">
                      <circle cx="38" cy="38" r="35" fill="none" stroke="#F1F5F9" strokeWidth="4" data-id="element-1102" />
                      <circle cx="38" cy="38" r="35" fill="none" stroke="#A855F7" strokeWidth="4" strokeDasharray="220" strokeDashoffset={220 - progressToNextLevel / 100 * 220} strokeLinecap="round" data-id="element-1103" />
                    </svg>

                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-black text-3xl border-4 border-white shadow-sm relative z-10" data-id="element-1104">
                      {displayName.charAt(0)}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-amber-400 text-white text-xs font-black px-2 py-1 rounded-full border-2 border-white shadow-md flex items-center gap-1 z-20" data-id="element-1105">
                      <Star className="w-3 h-3 fill-current" data-id="element-1106" />
                      {stats.level}
                    </div>
                  </div>

                  <div className="relative z-10" data-id="element-1107">
                    <div className="font-black text-slate-800 text-xl mb-1" data-id="element-1108">
                      {displayName}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1109">
                      <span className="text-sm font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100" data-id="element-1110">
                        {stats.xp} XP
                      </span>
                    </div>
                  </div>
                </div>}

              {/* User Profile - Parent/Teacher/Admin */}
              {activeAccountRole !== 'learner' && <div className="flex items-center gap-4 p-5 bg-white rounded-3xl border-2 border-slate-100 shadow-sm relative overflow-hidden" data-id="element-1111">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-black text-2xl border-2 border-white shadow-sm flex-shrink-0" data-id="element-1112">
                    {displayName.charAt(0)}
                  </div>
                  <div data-id="element-1113">
                    <div className="font-black text-slate-800 text-xl mb-1" data-id="element-1114">
                      {displayName}
                    </div>
                    <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100" data-id="element-1115">
                      {getAccountRoleLabel(activeAccountRole)}
                    </span>
                  </div>
                </div>}

              {/* Navigation */}
              <nav className="space-y-2.5" data-id="element-1116">
                <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-3 block px-2" data-id="element-1117">
                  Menu
                </label>
                {navItems.map(item => {
              const isActive = currentView === item.id;
              let activeColor = 'bg-orange-100 text-orange-700 border-orange-200 shadow-sm';
              let iconColor = isActive ? 'text-orange-600' : 'text-slate-400';
              if (isActive) {
                if (item.id === 'home') {
                  activeColor = 'bg-blue-100 text-blue-700 border-blue-200 shadow-sm';
                  iconColor = 'text-blue-600';
                }
                if (item.id === 'modules') {
                  activeColor = 'bg-emerald-100 text-emerald-700 border-emerald-200 shadow-sm';
                  iconColor = 'text-emerald-600';
                }
                if (item.id === 'progress') {
                  activeColor = 'bg-amber-100 text-amber-700 border-amber-200 shadow-sm';
                  iconColor = 'text-amber-600';
                }
                if (item.id === 'assessment') {
                  activeColor = 'bg-orange-100 text-orange-700 border-orange-200 shadow-sm';
                  iconColor = 'text-orange-600';
                }
                if (item.id === 'learningLab') {
                  activeColor = 'bg-purple-100 text-purple-700 border-purple-200 shadow-sm';
                  iconColor = 'text-purple-600';
                }
              }
              return <button key={item.id} onClick={() => {
                onNavigate(item.id);
                onClose();
              }} className={`
                        w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-200 border-2
                        ${isActive ? activeColor : 'bg-white text-slate-600 border-transparent hover:border-slate-200 hover:bg-slate-50'}
                      `} data-id="element-1118">
                      <item.icon className={`w-7 h-7 ${iconColor}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1119" />
                      <span className="font-black text-lg" data-id="element-1120">{item.label}</span>
                    </button>;
            })}
              </nav>
            </div>

            {/* Footer */}
            <div className="p-6 border-t-2 border-slate-100 bg-white rounded-br-[2.5rem]" data-id="element-1121">
              <button onClick={() => {
            onClose();
            onLogout?.();
          }} className="flex items-center justify-center gap-3 text-slate-500 hover:text-rose-600 font-bold w-full p-4 rounded-2xl hover:bg-rose-50 border-2 border-transparent hover:border-rose-200 transition-colors" data-id="element-1122">
                <LogOut className="w-6 h-6 stroke-[2.5]" data-id="element-1123" />
                <span className="text-lg" data-id="element-1124">Uitloggen</span>
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}